/**
 * Session-scoped cache for @-mention autocomplete data (church members + groups).
 *
 * Why this exists:
 *   Each post composer / comment input previously fetched its own copy of the
 *   members + groups list. With multiple PostCards on screen, that meant 4-8
 *   redundant network calls per page load. This module dedupes that to ONE
 *   call per data type per church per page session.
 *
 * Behavior:
 *   - First call for a churchId triggers a fetch.
 *   - In-flight calls for the same churchId share the same promise (no
 *     duplicate requests even when multiple components ask simultaneously).
 *   - Successful results cached for the rest of the page session.
 *   - Failures are NOT cached — next call retries.
 *   - peek* helpers return cached data synchronously (or null) so React state
 *     can be initialized without a re-render flash on subsequent components.
 *
 * Lifetime:
 *   Lives in JS module memory. Cleared on page reload. Acceptable staleness:
 *   if a member joins/leaves the church mid-session, the change won't appear
 *   in @ suggestions until the next reload. Matches Slack/Notion behavior.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const membersCache = new Map(); // church_id -> Member[]
const groupsCache = new Map();  // church_id -> Group[]
const pendingMembers = new Map(); // church_id -> Promise<Member[]>
const pendingGroups = new Map();  // church_id -> Promise<Group[]>

/** Synchronous read — returns cached members for the church, or null if not cached. */
export function peekMembers(churchId) {
  if (!churchId) return null;
  return membersCache.has(churchId) ? membersCache.get(churchId) : null;
}

/** Synchronous read — returns cached groups for the church, or null if not cached. */
export function peekGroups(churchId) {
  if (!churchId) return null;
  return groupsCache.has(churchId) ? groupsCache.get(churchId) : null;
}

/** Async fetch of mentionable members. Cached + deduped per churchId.
 *
 * Returns:
 *   - Array (possibly empty) on success → cached
 *   - `null` on transient failure (network error, non-OK status) → NOT cached
 *
 * Callers MUST distinguish `null` (retry on next render) from `[]`
 * (legitimately no members; treat as loaded). Without this, a single
 * transient fetch failure would otherwise poison the component's state
 * (set to `[]`), causing the `!== null` guard to permanently early-return
 * and the @mention dropdown would never repopulate that session.
 */
export async function getMentionMembers(churchId) {
  if (!churchId) return [];
  if (membersCache.has(churchId)) return membersCache.get(churchId);
  if (pendingMembers.has(churchId)) return pendingMembers.get(churchId);

  const promise = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/church/${churchId}/members`);
      if (!res.ok) return null;
      const data = await res.json();
      const members = data?.members || [];
      membersCache.set(churchId, members);
      return members;
    } catch (err) {
      console.error('[mentionDataCache] Error fetching members:', err);
      return null;
    } finally {
      pendingMembers.delete(churchId);
    }
  })();

  pendingMembers.set(churchId, promise);
  return promise;
}

/** Async fetch of mentionable groups. Cached + deduped per churchId.
 *  Same null-on-failure / array-on-success contract as getMentionMembers.
 */
export async function getMentionGroups(churchId) {
  if (!churchId) return [];
  if (groupsCache.has(churchId)) return groupsCache.get(churchId);
  if (pendingGroups.has(churchId)) return pendingGroups.get(churchId);

  const promise = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/groups/autocomplete?church_id=${churchId}&q=`);
      if (!res.ok) return null;
      const data = await res.json();
      const groups = Array.isArray(data) ? data : [];
      groupsCache.set(churchId, groups);
      return groups;
    } catch (err) {
      console.error('[mentionDataCache] Error fetching groups:', err);
      return null;
    } finally {
      pendingGroups.delete(churchId);
    }
  })();

  pendingGroups.set(churchId, promise);
  return promise;
}
